import { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Loader2, Database, Server, Globe, User, RefreshCw, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';
import { Button } from '../components/Button';
import { API_URL, supabase } from '../lib/supabase';

interface ConnectionStatus {
  status: 'checking' | 'connected' | 'error';
  message: string;
  latency?: number;
}

export function TestPage() {
  const { user, profile, signOut } = useAuth();
  const { request } = useApi();
  
  const [supabaseStatus, setSupabaseStatus] = useState<ConnectionStatus>({ status: 'checking', message: 'Checking...' });
  const [backendStatus, setBackendStatus] = useState<ConnectionStatus>({ status: 'checking', message: 'Checking...' });
  const [dbStatus, setDbStatus] = useState<ConnectionStatus>({ status: 'checking', message: 'Checking...' });
  const [authStatus, setAuthStatus] = useState<ConnectionStatus>({ status: 'checking', message: 'Checking...' });

  const checkConnections = async () => {
    // Reset all statuses
    setSupabaseStatus({ status: 'checking', message: 'Checking...' });
    setBackendStatus({ status: 'checking', message: 'Checking...' });
    setDbStatus({ status: 'checking', message: 'Checking...' });
    setAuthStatus({ status: 'checking', message: 'Checking...' });

    // Check Supabase direct connection
    try {
      const start = Date.now();
      const { error } = await supabase.from('profiles').select('count').limit(1);
      const latency = Date.now() - start;
      
      if (error && error.code !== 'PGRST116') throw error;
      setSupabaseStatus({ 
        status: 'connected', 
        message: `Connected to Supabase`,
        latency 
      });
    } catch (err) {
      setSupabaseStatus({ 
        status: 'error', 
        message: err instanceof Error ? err.message : 'Failed to connect' 
      });
    }

    // Check Backend API
    try {
      const start = Date.now();
      const response = await fetch(`${API_URL}/api/health`);
      const latency = Date.now() - start;
      const data = await response.json();
      
      if (!response.ok) throw new Error(data.error);
      setBackendStatus({ 
        status: 'connected', 
        message: `${data.service} is running`,
        latency 
      });
    } catch (err) {
      setBackendStatus({ 
        status: 'error', 
        message: err instanceof Error ? err.message : 'Failed to connect' 
      });
    }

    // Check Database via Backend
    try {
      const start = Date.now();
      const response = await fetch(`${API_URL}/api/health/db`);
      const latency = Date.now() - start;
      const data = await response.json();
      
      if (!response.ok) throw new Error(data.error);
      setDbStatus({ 
        status: 'connected', 
        message: 'Database is connected',
        latency: parseInt(data.latency) || latency
      });
    } catch (err) {
      setDbStatus({ 
        status: 'error', 
        message: err instanceof Error ? err.message : 'Failed to connect' 
      });
    }

    // Check Auth via Backend
    try {
      const start = Date.now();
      const data = await request<{ user: { id: string; email: string } }>('/api/auth/me');
      const latency = Date.now() - start;
      
      setAuthStatus({ 
        status: 'connected', 
        message: `Authenticated as ${data.user.email}`,
        latency 
      });
    } catch (err) {
      setAuthStatus({ 
        status: 'error', 
        message: err instanceof Error ? err.message : 'Not authenticated' 
      });
    }
  };

  useEffect(() => {
    checkConnections();
  }, []);

  const StatusCard = ({ 
    title, 
    icon: Icon, 
    status 
  }: { 
    title: string; 
    icon: typeof Database; 
    status: ConnectionStatus 
  }) => (
    <div className={`p-6 rounded-xl border ${
      status.status === 'connected' 
        ? 'bg-green-500/10 border-green-500/30' 
        : status.status === 'error'
        ? 'bg-red-500/10 border-red-500/30'
        : 'bg-white/5 border-white/10'
    }`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${
            status.status === 'connected' 
              ? 'bg-green-500/20 text-green-400' 
              : status.status === 'error'
              ? 'bg-red-500/20 text-red-400'
              : 'bg-white/10 text-white/60'
          }`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-medium text-white">{title}</h3>
            <p className={`text-sm ${
              status.status === 'connected' 
                ? 'text-green-400' 
                : status.status === 'error'
                ? 'text-red-400'
                : 'text-white/60'
            }`}>
              {status.message}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {status.latency && (
            <span className="text-xs text-white/40">{status.latency}ms</span>
          )}
          {status.status === 'checking' ? (
            <Loader2 className="w-5 h-5 text-white/60 animate-spin" />
          ) : status.status === 'connected' ? (
            <CheckCircle className="w-5 h-5 text-green-400" />
          ) : (
            <XCircle className="w-5 h-5 text-red-400" />
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-primary-900 to-slate-900 p-8">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/2 -right-1/2 w-full h-full bg-gradient-to-br from-primary-500/10 to-accent-500/10 blur-3xl rounded-full" />
        <div className="absolute -bottom-1/2 -left-1/2 w-full h-full bg-gradient-to-tr from-accent-500/10 to-primary-500/10 blur-3xl rounded-full" />
      </div>

      <div className="relative max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary-500 to-accent-500 rounded-2xl mb-6 shadow-lg shadow-primary-500/30">
            <Globe className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-3">System Health Check</h1>
          <p className="text-white/60 text-lg">Verifying all connections are working properly</p>
        </div>

        {/* User info */}
        {user && (
          <div className="mb-8 p-6 bg-white/5 rounded-xl border border-white/10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-accent-500 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-medium text-white">{profile?.full_name || 'User'}</p>
                  <p className="text-sm text-white/60">{user.email}</p>
                </div>
              </div>
              <Button variant="ghost" onClick={signOut} size="sm">
                <LogOut className="w-4 h-4" />
                Sign out
              </Button>
            </div>
          </div>
        )}

        {/* Status cards */}
        <div className="space-y-4 mb-8">
          <StatusCard title="Supabase (Direct)" icon={Database} status={supabaseStatus} />
          <StatusCard title="Backend API" icon={Server} status={backendStatus} />
          <StatusCard title="Database via Backend" icon={Database} status={dbStatus} />
          <StatusCard title="Authentication" icon={User} status={authStatus} />
        </div>

        {/* Refresh button */}
        <div className="text-center">
          <Button onClick={checkConnections} variant="secondary">
            <RefreshCw className="w-4 h-4" />
            Refresh Status
          </Button>
        </div>

        {/* Summary */}
        <div className="mt-12 p-6 bg-white/5 rounded-xl border border-white/10">
          <h2 className="text-lg font-semibold text-white mb-4">Connection Summary</h2>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-white/60">Frontend</p>
              <p className="text-white font-mono">Vite + React + TypeScript</p>
            </div>
            <div>
              <p className="text-white/60">Backend URL</p>
              <p className="text-white font-mono text-xs">{API_URL}</p>
            </div>
            <div>
              <p className="text-white/60">Supabase Project</p>
              <p className="text-white font-mono text-xs">elovquwpbwdayzzywaar</p>
            </div>
            <div>
              <p className="text-white/60">User ID</p>
              <p className="text-white font-mono text-xs">{user?.id || 'Not authenticated'}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
